import { Dispatch, FC, SetStateAction, useEffect, useState } from 'react';

import { DesktopDatePicker as MuiDesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { ReactComponent as ArrowDownIcon } from 'assett/icons/arrow_down_icon.svg';
import { ReactComponent as ArrowlefttIcon } from 'assett/icons/arrow_left_datepicker_icon.svg';
import { ReactComponent as ArrowRightIcon } from 'assett/icons/arrow_right_datepicker_icon.svg';
import { ReactComponent as ArrowUpIcon } from 'assett/icons/arrow_up_icon.svg';
import dayjs, { Dayjs } from 'dayjs';
import styled from 'styled-components';
import { useTheme } from 'stylesHooks';
import { OptionsDatepicker } from 'types/Form';
import { getDateFormat, getLanguageStorage } from 'utils/localStorage';
import { useTranslation } from 'utils/translations';
import { dayjsForMaterial } from 'utils/utils';

type Props = {
	value: dayjs.Dayjs | null;
	onChange: (value: dayjs.Dayjs | null) => void;
	label: string;
	variant: keyof typeof OptionsDatepicker;
	minDate?: dayjs.Dayjs | undefined;
	maxDate?: dayjs.Dayjs | undefined;
	setErrorDate?: Dispatch<SetStateAction<boolean>>;
	error?: boolean;
	disabled?: boolean;
};

const DatePicker: FC<Props> = props => {
	const { t: tClientLabels } = useTranslation('', {
		keyPrefix: 'clientLabels',
	});
	const {
		value,
		onChange,
		label,
		variant,
		maxDate: maxDateNF,
		minDate: mindateNF,
		setErrorDate,
		error,
	} = props;

	const minDate = dayjsForMaterial(mindateNF);
	const maxDate = dayjsForMaterial(maxDateNF);

	const [open, setOpen] = useState<boolean>(false);
	const theme = useTheme();

	const [invalidDate, setInvalidDate] = useState(false);

	theme.datepicker = OptionsDatepicker[props.variant as OptionsDatepicker];

	useEffect(() => {
		if (!!setErrorDate) {
			setErrorDate(invalidDate);
		}
	}, [invalidDate, setErrorDate]);

	return (
		<DesktopDatePicker
			{...props}
			key={`datepicker_${variant}`}
			format={getDateFormat()}
			maxDate={maxDate}
			minDate={minDate}
			open={open}
			onOpen={() => setOpen(true)}
			onClose={() => setOpen(false)}
			slots={{
				openPickerIcon: () => (!open ? <ArrowDownIcon /> : <ArrowUpIcon />),
				leftArrowIcon: () => <ArrowlefttIcon />,
				rightArrowIcon: () => <ArrowRightIcon />,
			}}
			label={label}
			value={value}
			onChange={e => {
				const newDate = dayjsForMaterial(e as Dayjs);
				const isValidDate =
					!newDate ||
					(typeof newDate?.isValid() !== 'undefined' &&
						newDate.isValid() &&
						(!maxDate || maxDate.diff(newDate) >= 0) &&
						(!minDate || newDate.diff(minDate) >= 0));
				onChange(isValidDate && newDate ? newDate : null);
				setOpen(false);
				if (isValidDate || value) {
					setInvalidDate(false);
				} else setInvalidDate(true);
			}}
			showDaysOutsideCurrentMonth
			slotProps={{
				textField: {
					fullWidth: true,
					variant: 'filled' as const,
					autoComplete: 'off',
					sx: open
						? {
								border: `1px solid ${theme.palette.primary.main}`,
								'& .MuiInputBase-root': {
									border: 'none',
								},
							}
						: {},
					InputLabelProps: {
						style: { transform: 'translate(12px 18px)' },
					},
					error: invalidDate || error,
					inputProps: {
						placeholder:
							getLanguageStorage() !== 'en-us'
								? `${tClientLabels('commons.ddmmyyyy')}`
								: `${tClientLabels('commons.mmddyyyy')}`,
					},
					InputProps: {
						disableUnderline: true,
					},
				},
				popper: {
					placement: 'bottom',
				},
				desktopPaper: {
					style: {
						margin: '5px auto 0',
					},
				},
			}}
		/>
	);
};

const DesktopDatePicker = styled(MuiDesktopDatePicker)`
	${({ open, theme }) =>
		open ? `border: 1px solid ${theme.palette.primary.main}` : ``};
	${({ theme }) =>
		`
		.Mui-disabled {
			background-color: ${theme.palette.grey6.main} !important;
			color: ${theme.palette.primary.main};
			-webkit-text-fill-color: ${theme.palette.primary.main};
			border-color: ${theme.palette.grey4.main};
		}
		.MuiInputLabel-root.Mui-disabled {
			-webkit-text-fill-color: ${theme.palette.grey2.main};
			color: ${theme.palette.grey2.main};
		}
		.MuiInputAdornment-root.Mui-disabled {visibility: hidden}
		
	`};
`;

export default DatePicker;
